//  formatCurrency(price, locale, currency)
/* eslint-disable @typescript-eslint/no-explicit-any */
import { IconType } from "react-icons";
import {
  FaRupeeSign,
  FaDollarSign,
  FaEuroSign,
  FaPoundSign,
  FaYenSign,
  FaWonSign,
  FaRubleSign,
  FaShekelSign,
  FaHryvnia,
  FaLiraSign,
  FaMoneyBillWave,
} from "react-icons/fa";

/**
 * CHANGE: the symbol used to come from a hand-written CURRENCY_SYMBOLS map, so
 * every currency an admin added in Admin Config needed a matching code change
 * or it fell back to showing the bare code. The symbol is now DERIVED from the
 * code via Intl, which knows every ISO 4217 currency — nothing to maintain.
 *
 * `currencyDisplay: "symbol"` is deliberately the same display `formatCurrency`
 * uses, so the standalone symbol and the formatted amount can never disagree
 * (the old map returned "S$" for SGD while formatCurrency printed "SGD").
 */
// A blank/whitespace-only unit is treated the same as a missing one, so every
// helper below falls back identically.
const normalizeCode = (currency?: string | null, fallback = ""): string => {
  const code = currency ? String(currency).trim().toUpperCase() : "";
  return code || fallback;
};

/**
 * Pulls the currency part out of a formatted amount. `currencyDisplay` picks
 * how verbose that part is: "symbol" is the standard display, "narrowSymbol"
 * prefers a bare glyph (₽, ฿, ₦) where one exists.
 *
 * Returns "" when the code is not one Intl can format.
 */
const extractCurrencyPart = (
  code: string,
  currencyDisplay: "symbol" | "narrowSymbol",
): string => {
  try {
    return (
      new Intl.NumberFormat("en-US", { style: "currency", currency: code, currencyDisplay })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? ""
    );
  } catch {
    // Intl throws a RangeError on a malformed code (wrong length, digits, …).
    return "";
  }
};

// True when Intl gave back the plain ISO code (e.g. "NGN") rather than a real
// symbol — letters only, so "A$", "CN¥" and "₦" are all excluded.
const isBareCode = (value: string): boolean => /^[A-Z]+$/.test(value);

/**
 * Picks which `currencyDisplay` to use for a code.
 *
 * "symbol" is the default because it disambiguates the dollar family (AUD "A$",
 * CAD "CA$", MXN "MX$") and the yen family (CNY "CN¥"). But for currencies whose
 * standard display is just the code — NGN, RUB, THB, TRY — the narrow form has
 * a proper glyph (₦, ₽, ฿, ₺), which is what belongs in an input prefix.
 *
 * Codes with no glyph at all (AED, CHF, ZAR) keep the code in both forms.
 */
const resolveCurrencyDisplay = (code: string): "symbol" | "narrowSymbol" => {
  const symbol = extractCurrencyPart(code, "symbol");
  if (!symbol || !isBareCode(symbol)) return "symbol";

  const narrow = extractCurrencyPart(code, "narrowSymbol");
  return narrow && !isBareCode(narrow) ? "narrowSymbol" : "symbol";
};

// Intl.NumberFormat construction is not free and these run on every render, so
// resolved symbols and displays are memoised per code.
const displayCache = new Map<string, "symbol" | "narrowSymbol">();

const currencyDisplayFor = (code: string): "symbol" | "narrowSymbol" => {
  const cached = displayCache.get(code);
  if (cached !== undefined) return cached;

  const display = resolveCurrencyDisplay(code);
  displayCache.set(code, display);
  return display;
};

const symbolCache = new Map<string, string>();

/**
 * Returns the currency symbol for a given currency type/code.
 * Reusable across the app so the symbol is never hardcoded per screen.
 *
 * @param currency - ISO currency code (e.g. "INR", "USD"). Case-insensitive.
 *                   Any valid ISO code works, including ones added later.
 * @returns the matching symbol (e.g. "₹", "$"); the code itself (uppercased)
 *          when Intl has no symbol for it, or "" when nothing is passed.
 */
export const getCurrencySymbol = (currency?: string | null): string => {
  const code = normalizeCode(currency);
  if (!code) return "";

  const cached = symbolCache.get(code);
  if (cached !== undefined) return cached;

  // Fall back to the code so an unknown currency still reads sensibly.
  const symbol = extractCurrencyPart(code, currencyDisplayFor(code)) || code;
  symbolCache.set(code, symbol);
  return symbol;
};

/**
 * NOTE: icons are matched on the currency's GLYPH rather than its code, so a
 * newly added currency picks up the right icon with no code change — every
 * dollar-family code (AUD "A$", CAD "CA$", BRL "R$") contains "$", and CNY
 * ("CN¥") contains "¥". Only glyphs react-icons/fa actually ships are listed.
 */
const GLYPH_ICONS: { glyph: string; icon: IconType }[] = [
  { glyph: "₹", icon: FaRupeeSign },
  { glyph: "€", icon: FaEuroSign },
  { glyph: "£", icon: FaPoundSign },
  { glyph: "¥", icon: FaYenSign },
  { glyph: "₩", icon: FaWonSign },
  { glyph: "₽", icon: FaRubleSign },
  { glyph: "₪", icon: FaShekelSign },
  { glyph: "₴", icon: FaHryvnia },
  { glyph: "₺", icon: FaLiraSign },
  // "$" is last so a prefixed dollar (A$, CA$, R$) only matches after the more
  // specific glyphs above have been ruled out.
  { glyph: "$", icon: FaDollarSign },
];

const iconCache = new Map<string, IconType>();

/**
 * Returns the icon component for a given currency type/code, so amount fields
 * never hardcode a single currency's glyph.
 *
 * @param currency - ISO currency code (e.g. "INR", "USD"). Case-insensitive.
 * @returns the react-icons component for the currency's glyph, or
 *          `FaMoneyBillWave` when it has no dedicated icon (e.g. AED, CHF).
 *          A missing code is treated as "INR", which mirrors `formatCurrency`'s
 *          own default so the icon and the formatted amount cannot disagree.
 */
export const getCurrencyIcon = (currency?: string | null): IconType => {
  const code = normalizeCode(currency, "INR");

  const cached = iconCache.get(code);
  if (cached !== undefined) return cached;

  // "narrowSymbol" is preferred here because it resolves to a bare glyph for
  // currencies whose standard display is the plain code (RUB → "₽", THB → "฿"),
  // giving the icon lookup more to match on.
  const glyph = extractCurrencyPart(code, "narrowSymbol") || extractCurrencyPart(code, "symbol");
  const icon = GLYPH_ICONS.find((entry) => glyph.includes(entry.glyph))?.icon ?? FaMoneyBillWave;

  iconCache.set(code, icon);
  return icon;
};

// CHANGE: named constant instead of a bare `2` at each fallback below, so the
// two manual-formatting paths can't drift apart.
// NOTE: only the non-ISO fallback uses it — the Intl paths keep each currency's
// own precision (JPY has none), which is why this isn't applied globally.
const DEFAULT_FRACTION_DIGITS = 2;

// Locale overrides for codes whose grouping differs from the en-US default
// (INR uses the lakh/crore grouping). Everything else formats fine with en-US.
const CURRENCY_LOCALES: Record<string, string> = {
  INR: "en-IN",
  USD: "en-US",
};

/**
 * Formats an amount with the currency's grouping and decimal rules but WITHOUT
 * its symbol — for fields that render the symbol separately (e.g. a read-only
 * box that mirrors the `Input` component's `prefix`, where "₹900.00" would sit
 * flush against the prefix with no gap).
 *
 * Built from `formatToParts` rather than a fixed 2 decimals so each currency
 * keeps its own precision (JPY has none) and its own grouping.
 *
 * @returns the grouped amount, e.g. "900.00"; "" when the value isn't numeric.
 */
export const formatAmount = (value: any, currency = "INR"): string => {
  const code = normalizeCode(currency, "INR");

  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "";

  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] ?? "en-US", {
      style: "currency",
      currency: code,
    })
      .formatToParts(numeric)
      // Drop the symbol itself and the separator Intl puts beside it.
      .filter((part) => part.type !== "currency" && part.type !== "literal")
      .map((part) => part.value)
      .join("");
  } catch {
    // Intl throws on a non-ISO code; two decimals is a safe generic default.
    return numeric.toFixed(DEFAULT_FRACTION_DIGITS);
  }
};

/**
 * CHANGE: this previously formatted ONLY INR and USD and returned the raw,
 * unformatted number for every other code — so a EUR/GBP/AED claim rendered as
 * a bare "10" with no symbol and no decimals. It now formats any ISO code via
 * Intl, the same source the symbol and icon helpers above derive from, so all
 * three stay in step for any currency Admin Config offers.
 *
 * INR and USD output is unchanged, including `null`/`""` → "₹0.00", so the
 * existing table cells keep rendering exactly as before. Only a genuinely
 * non-numeric value (undefined, NaN) changes: it returns "" rather than the old
 * "₹NaN". A code Intl does not recognise falls back to symbol + amount.
 */
const formatCurrency = (value: any, currency = "INR") => {
  const code = normalizeCode(currency, "INR");

  // `Number(null)` / `Number("")` are 0, which is what this used to print; only
  // a value that cannot be a number at all is blanked, so callers interpolating
  // the result never show "₹NaN".
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "";

  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[code] ?? "en-US", {
      style: "currency",
      currency: code,
      // Same display getCurrencySymbol resolves, so a standalone symbol and a
      // formatted amount always show the identical glyph.
      currencyDisplay: currencyDisplayFor(code),
    }).format(numeric);
  } catch {
    // Intl throws on a non-ISO code; keep the amount readable regardless.
    return `${getCurrencySymbol(code)}${numeric.toFixed(DEFAULT_FRACTION_DIGITS)}`;
  }
};

export default formatCurrency;
